import React, { useState, useCallback } from "react";
import {
  Select,
  DatePicker,
  Pagination,
  Spin,
  Empty,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  FilterOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import drEasyApi from "../../../Api/Customapi";
import { useLanguage } from "../../../context/LanguageContext";

const { RangePicker } = DatePicker;

// ─── API ──────────────────────────────────────────────────────────────────────
const fetchMyAccount = async (params) => {
  const res = await drEasyApi.get("/api/Invoice/GetMyAccount", { params });
  return res.data;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, currency) => `${currency} ${Number(n ?? 0).toFixed(2)}`;

const isPaidStatus = (invoice) => {
  const remaining = (invoice.totals?.totalAmount ?? 0) - (invoice.paidAmount ?? 0);
  return remaining <= 0;
};

// ─── Invoice Card ─────────────────────────────────────────────────────────────
const InvoiceCard = ({ invoice, index, t, currency }) => {
  const [expanded, setExpanded] = useState(false);
  const paid      = isPaidStatus(invoice);
  const total     = invoice.totals?.totalAmount ?? 0;
  const remaining = Math.max(0, total - (invoice.paidAmount ?? 0));
  const paidPct   = total > 0 ? Math.min(100, (invoice.paidAmount / total) * 100) : 100;
  const hasServices = invoice.services?.length > 0;
  const hasDiscount =
    (invoice.discountAmount ?? 0) > 0 || (invoice.couponDiscountAmount ?? 0) > 0;

  return (
    <div
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
      style={{ animationDelay: `${index * 60}ms`, animation: "fadeUp .45s ease both" }}
    >
      {/* ── Top accent bar ── */}
      <div
        className={`h-1 w-full ${paid
          ? "bg-green-500"
          : "bg-gradient-to-r from-amber-400 to-orange-400"}`}
      />

      <div className="p-5">
        {/* ── Header row ── */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <FileTextOutlined className="text-primary text-sm" />
              <span className="text-xs text-gray-400 font-mono">
                {invoice.id?.slice(0, 8).toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <CalendarOutlined />
              {dayjs(invoice.createdAt).format("MMM D, YYYY · h:mm A")}
            </p>
          </div>

          <div className="flex flex-col items-end gap-1">
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full
              ${paid
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-amber-50 text-amber-700 border border-amber-200"}`}
            >
              {paid ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              {paid ? t("myInvoices.card.paid") : t("myInvoices.card.pending")}
            </span>

            {invoice.applliedCoupoun && (
              <Tooltip title={`${t("myInvoices.card.coupon")}: ${invoice.applliedCoupoun}`}>
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-violet-50 text-violet-600 border border-violet-200 rounded-full">
                  <TagOutlined /> {invoice.applliedCoupoun}
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {/* ── Created by ── */}
        {/* <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
            {invoice.createdByName?.[0]?.toUpperCase() ?? "?"}
          </div>
          <span className="text-xs text-gray-500">
            {t("myInvoices.card.createdBy")}{" "}
            <span className="text-gray-700 font-medium">{invoice.createdByName}</span>
          </span>
        </div> */}

        {/* ── Amounts grid ── */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            {
              label: t("myInvoices.card.visit"),
              value: fmt(invoice.visitPrice, currency),
              color: "text-primary",
            },
            {
              label: t("myInvoices.card.paidLabel"),
              value: fmt(invoice.paidAmount, currency),
              color: "text-green-600",
            },
            {
              label:  t("myInvoices.card.due"),
              value: fmt(remaining, currency),
              color: remaining > 0 ? "text-orange-500" : "text-green-500",
            },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-gray-50 rounded-xl p-2.5 text-center">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
              <p className={`text-sm font-bold tabular-nums ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Progress bar ── */}
        {/* <div className="mb-3">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>{t("myInvoices.card.paymentProgress")}</span>
            <span>{Math.round(paidPct)}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${paid
                ? "bg-green-500"
                : "bg-gradient-to-r from-amber-400 to-orange-400"}`}
              style={{ width: `${paidPct}%` }}
            />
          </div>
        </div> */}

        {/* ── Discounts ── */}
        {hasDiscount && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {invoice.discountAmount > 0 && (
              <span className="text-[11px] bg-red-50 text-red-500 border border-red-100 px-2 py-0.5 rounded-full">
                −{fmt(invoice.discountAmount, currency)} {t("myInvoices.card.discount")}
              </span>
            )}
            {invoice.couponDiscountAmount > 0 && (
              <span className="text-[11px] bg-violet-50 text-violet-600 border border-violet-100 px-2 py-0.5 rounded-full">
                −{fmt(invoice.couponDiscountAmount, currency)} {t("myInvoices.card.coupon")}
              </span>
            )}
          </div>
        )}

        {/* ── Total ── */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500 font-medium">
            {t("myInvoices.card.totalAmount")}
          </span>
          <span className="text-lg font-bold text-gray-800 tabular-nums">
            {fmt(total, currency)}
          </span>
        </div>

        {/* ── Services accordion ── */}
        {hasServices && (
          <div className="mt-3">
            <button
              onClick={() => setExpanded((p) => !p)}
              className="w-full text-xs text-primary hover:text-primary_hover flex items-center justify-between py-1 bg-transparent border-none cursor-pointer"
            >
              <span>
                {invoice.services.length}{" "}
                {invoice.services.length > 1
                  ? t("myInvoices.card.services_plural")
                  : t("myInvoices.card.services")}
              </span>
              <span
                className="transition-transform duration-200"
                style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
              >
                ▾
              </span>
            </button>

            {expanded && (
              <div className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
                {invoice.services.map((s, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <span className="truncate max-w-[160px]">
                      {s.title || s.externalServiceTitle || t("myInvoices.card.services")}
                    </span>
                    <span className="font-semibold text-gray-700 ml-2 shrink-0">
                      {s.quantity} × {fmt(s.unitPrice, currency)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────
const MyInvoicesScreen = () => {
  const { t } = useLanguage();
  const currency = t("common.currency");

  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 12,
    isPaid: undefined,
    fromDate: undefined,
    toDate: undefined,
    sortDescending: true,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["myInvoices", filters],
    queryFn: () =>
      fetchMyAccount({
        PageNumber: filters.pageNumber,
        PageSize: filters.pageSize,
        IsPaid: filters.isPaid,
        FromDate: filters.fromDate,
        ToDate: filters.toDate,
        SortBy: "SortDescending",
        SortDescending: filters.sortDescending,
      }),
    keepPreviousData: true,
  });

  const invoices   = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const update = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch, pageNumber: 1 }));
  }, []);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const stats = React.useMemo(() => {
    if (!invoices.length) return null;
    const totalAmt  = invoices.reduce((s, i) => s + (i.totals?.totalAmount ?? 0), 0);
    const totalPaid = invoices.reduce((s, i) => s + (i.paidAmount ?? 0), 0);
    const paidCount = invoices.filter(isPaidStatus).length;
    return { totalAmt, totalPaid, remaining: totalAmt - totalPaid, paidCount };
  }, [invoices]);

  return (
    <div className="min-h-screen md:max-w-7xl mx-auto bg-gradient-to-br from-slate-50 via-cyan-50/30 to-indigo-50/40 p-4 sm:p-6">
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-0.5">
          {t("myInvoices.title")}
        </h1>
        <p className="text-sm text-gray-500">{t("myInvoices.subtitle")}</p>
      </div>

      {/* ── Summary cards ── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: t("myInvoices.summary.totalBilled"),  value: fmt(stats.totalAmt, currency),  bg: "from-primary/10 to-primary/5",   text: "text-primary" },
            { label: t("myInvoices.summary.totalPaid"),    value: fmt(stats.totalPaid, currency),  bg: "from-emerald-100 to-emerald-50", text: "text-emerald-700" },
            { label: t("myInvoices.summary.outstanding"),  value: fmt(stats.remaining, currency),  bg: "from-orange-100 to-orange-50",   text: "text-orange-600" },
            // { label: t("myInvoices.summary.settled"),      value: `${stats.paidCount}/${invoices.length}`, bg: "from-sky-100 to-sky-50", text: "text-sky-700" },
          ].map(({ label, value, bg, text }) => (
            <div key={label} className={`bg-gradient-to-br ${bg} rounded-2xl p-4 border border-white shadow-sm`}>
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-lg font-bold tabular-nums ${text}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <FilterOutlined className="text-gray-400" />

          <Select
            placeholder={t("myInvoices.filters.paymentStatus")}
            allowClear
            value={filters.isPaid}
            onChange={(v) => update({ isPaid: v })}
            className="w-40"
            options={[
              { value: true,  label: t("myInvoices.filters.paid") },
              { value: false, label: t("myInvoices.filters.pending") },
            ]}
          />

          <RangePicker
            format="MMM D, YYYY"
            onChange={(dates) =>
              update({
                fromDate: dates?.[0]?.toISOString(),
                toDate:   dates?.[1]?.toISOString(),
              })
            }
            className="rounded-lg"
            placeholder={[t("myInvoices.filters.fromDate"), t("myInvoices.filters.toDate")]}
          />

          <Select
            value={filters.sortDescending}
            onChange={(v) => update({ sortDescending: v })}
            className="w-36"
            options={[
              { value: true,  label: t("myInvoices.filters.newestFirst") },
              { value: false, label: t("myInvoices.filters.oldestFirst") },
            ]}
          />

          <span className="ml-auto text-xs text-gray-400">
            {t("myInvoices.filters.invoiceCount", { count: totalCount })}
          </span>
        </div>
      </div>

      {/* ── Content ── */}
      <Spin spinning={isLoading || isFetching}>
        {invoices.length === 0 && !isLoading ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-20 text-center">
            <Empty
              description={
                <span className="text-gray-400">{t("myInvoices.empty")}</span>
              }
            />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  gap-4 mb-6">
              {invoices.map((inv, i) => (
                <InvoiceCard
                  key={inv.id}
                  invoice={inv}
                  index={i}
                  t={t}
                  currency={currency}
                />
              ))}
            </div>

            {totalCount > filters.pageSize && (
              <div className="flex justify-center">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3">
                  <Pagination
                    current={filters.pageNumber}
                    pageSize={filters.pageSize}
                    total={totalCount}
                    onChange={(page, pageSize) =>
                      setFilters((prev) => ({ ...prev, pageNumber: page, pageSize }))
                    }
                    showSizeChanger
                    pageSizeOptions={["8", "12", "24"]}
                    showTotal={(total) =>
                      t("myInvoices.filters.invoiceCount", { count: total })
                    }
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Spin>
    </div>
  );
};

export default MyInvoicesScreen;